import {
    Component,
    ComponentFactoryResolver,
    Injector,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subscription, timer } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { User } from '../../user/user.model';
import { UserService } from '../../user/user.service';
import { TemplateConfig } from '../core/config.scorecard.model';
import { ScorecardDataService } from '../core/data.service';
import { ScorecardExportService } from '../core/export.service';
import { GridType, WidgetType } from '../core/template-definition.model';
import { deflate, inflate, InflateHandle } from '../core/template-inflater';
import { TranslationKeyMap } from '../scorecard.i18n';
import {
    CMPS_SCORECARD_INITIAL_GRID_TAB,
    CMPS_SCORECARD_INITIAL_GRID_TYPE,
    CMPS_SCORECARD_PAGE,
} from '../scorecard.metadata';
import { datasetKey, PeriodType } from '../scorecard.model';
import {
    ScorecardComponentComm,
    ScoredardBridgeService,
} from '../widgets/grid/sc-bridge.service';
import { ScorecardSelection } from '../widgets/grid/team-grid.model';

import { GridTab } from './grid-tab.model';
import {
    createTab,
    exportScorecard,
    listData,
    listDataCancel,
    reload,
    reloadCancel,
    removeTab,
    reset,
    resetGrid,
    selectedDirectReportOption,
    selectedGridTab,
    selectPeriodType,
    selectScorecard,
    selectTab,
    widgetsExport,
} from './scorecard.action';
import scorecardReducer from './scorecard.reducer';
import { ScorecardState } from './scorecard.state';
import { widgetTypeComponentMap } from './widget-type-component-map';

const DirectReportViewTranslationKeyMap = {
    [GridType.Rank_View]: TranslationKeyMap._9392_RANK_VIEW,
    [GridType.Direct_Reports_View]:
        TranslationKeyMap._9393_DIRECT_REPORT_SCORECARDS,
    [GridType.Contribution_View]: TranslationKeyMap._9394_CONTRIBUTION_VIEW,
};

@Component({
    selector: 'cmps-scorecard',
    templateUrl: './scorecard.component.html',
    host: {
        '[id]': '"cmps-scorecard"',
        '[class]': '"cmps-scorecard"',
    },
})
@TakeUntilDestroy()
@UseStore<ScorecardState>('scorecard.root', scorecardReducer.scorecard.root)
export class ScorecardComponent
    implements
        OnInit,
        OnDestroy,
        StateToProps<ScorecardState>,
        ScorecardComponentComm
{
    state: ScorecardState;

    @ViewChild('ref', { read: ViewContainerRef, static: true })
    private readonly _viewContainerRef: ViewContainerRef;

    @ViewChild('teamGridRef', { read: ViewContainerRef, static: true })
    private readonly _teamGridViewContainerRef: ViewContainerRef;

    private _inflateHdl: InflateHandle;
    private _inflateHdl2: InflateHandle; // TODO if kept, rename. maybe sync with _bridges[]
    /** @private */ _expandLabelToggle: boolean = true;
    /** @private */ _directReportViewTranslationKeyMap =
        DirectReportViewTranslationKeyMap;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _GridTab = GridTab;
    /** @private */ _longPendingReload: boolean;
    /** @private */ _reset: boolean = false;
    /** @private */ _showExpandAllBtn: boolean = false;
    /** @private */ _templateConfigCopy: TemplateConfig;
    private _longPendingReloadSub: Subscription;
    /** @private */ _fromInsightCard: boolean = false;
    /** @private */ _trendMode: boolean = false;

    public containerColour: string;

    // TODO revise, refactor. this is temporary in order to separate the grids
    // TODO refactor. each grid should be changed to be a WidgetType
    private readonly _bridges: ScoredardBridgeService[];
    private readonly _bridgeSubs: Subscription[];

    constructor(
        private readonly _injector: Injector,
        private readonly _factoryResolver: ComponentFactoryResolver,
        /** @private */ public _activatedRoute: ActivatedRoute,
        private readonly _authService: AuthService,
        private readonly _dataService: ScorecardDataService,
        private readonly _exportService: ScorecardExportService,
        private readonly _userService: UserService,
    ) {
        this._bridges = [null, null];
        this._bridgeSubs = [null, null];
    }

    ngOnInit(): void {
        combineLatest([
            this._activatedRoute.params,
            this._activatedRoute.queryParams,
        ])
            .pipe(
                tap(([params, queryParams]) => {
                    const userId =
                        String(params.userId).toLowerCase() === 'me'
                            ? this._authService.user.id
                            : parseInt(params.userId) ||
                              parseInt(queryParams.userId);
                    const scorecardId =
                        parseInt(params.scorecardId) ||
                        parseInt(queryParams.scorecardId);
                    const periodTypeId = parseInt(queryParams.periodTypeId);
                    this._fromInsightCard =
                        queryParams.fromInsightCard === 'true' ||
                        queryParams.fromInsightCard === '1';
                    this._trendMode =
                        queryParams.trendMode === 'true' ||
                        queryParams.trendMode === '1';
                    if (!isNaN(userId)) {
                        if (
                            this.state.tabs.size === 0 &&
                            userId !== this._authService.user.id
                        ) {
                            this._createTab({
                                userId: this._authService.user.id,
                            });
                        }

                        this._createTab(
                            {
                                userId,
                                scorecardId: !isNaN(scorecardId)
                                    ? scorecardId
                                    : null,
                            },
                            null,
                            !isNaN(periodTypeId) ? [periodTypeId] : null,
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._exportService.inflateHandle = null;
        deflate(this._inflateHdl2);
        deflate(this._inflateHdl);
        this._disposeBridges();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._longPendingReloadSub && this._longPendingReloadSub.unsubscribe();
    }

    stateToProps(prevState?: ScorecardState): void {
        this._reinflateCheck(prevState);

        this._longPendingReloadCheck(prevState);

        this._reloadCheck(prevState);

        // TODO consider updating the route/url after tab/scorecard change

        try {
            // TODO move _showExpandAllBtn into state
            if (
                (prevState.selectedTab == null &&
                    this.state.selectedTab != null) ||
                (prevState.selectedTab !== this.state.selectedTab &&
                    prevState.selectedTab.tabId !==
                        this.state.selectedTab.tabId) ||
                prevState.selectedTab.selectedDirectReportDropdownItem !==
                    this.state.selectedTab.selectedDirectReportDropdownItem
            ) {
                this._showExpandAllBtn = [
                    GridType.Contribution_View,
                    GridType.Direct_Reports_View,
                ].includes(
                    this.state.selectedTab.selectedDirectReportDropdownItem,
                );
            }
        } catch (err) {
            CMPS_APP_DEF.ENV !== 'prod' &&
                console.debug('stateToProps selectedDirectReportDropdownItem');
            CMPS_APP_DEF.ENV !== 'prod' && console.debug(err);
        }

        try {
            if (
                (prevState.selectedTab == null &&
                    this.state.selectedTab != null) ||
                (prevState.selectedTab !== this.state.selectedTab &&
                    prevState.selectedTab.tabId !==
                        this.state.selectedTab.tabId) ||
                prevState.selectedTab.selectedGridTab !==
                    this.state.selectedTab.selectedGridTab
            ) {
                this._bridges
                    .find((e) => e !== this.bridge)
                    ?.componentTeamgrid?.willBeHidden();

                /* CMPS_SCORECARD_INITIAL_GRID_TAB ensures it is set on instantiation
                even if the component isn't yet connected when reaching here.
                alternately, observableForConnect() could be used as well  */
                this.bridge.grid.onTabChange(
                    this.state.selectedTab.selectedGridTab,
                );
            }
        } catch (err) {
            CMPS_APP_DEF.ENV !== 'prod' &&
                console.debug('stateToProps grid.onTabChange');
            CMPS_APP_DEF.ENV !== 'prod' && console.debug(err);
        }

        try {
            if (
                prevState.selectedTab !== this.state.selectedTab ||
                prevState.selectedTab.selectedDirectReportDropdownItem !==
                    this.state.selectedTab.selectedDirectReportDropdownItem
            ) {
                /* CMPS_SCORECARD_INITIAL_GRID_TYPE ensures it is set on instantiation
                even if the component isn't yet connected when reaching here.
                alternately, observableForConnect() could be used as well */
                this.bridge.componentTeamgrid.onTabChange(
                    this.state.selectedTab.selectedDirectReportDropdownItem,
                );
            }
        } catch (err) {
            CMPS_APP_DEF.ENV !== 'prod' &&
                console.debug('stateToProps componentTeamgrid.onTabChange');
            CMPS_APP_DEF.ENV !== 'prod' && console.debug(err);
        }
    }

    propsToDispatch(): void {
        // TODO revise. this is temporary means of flushing tabs after auth user has changed
        if (
            this.state.authUserId != null &&
            this.state.authUserId !== this._authService.user.id
        ) {
            useDispatcher(this, reset({}));
        }
    }

    /** @private yes, keep this explicitly defined event handler */
    _onTabCreate(user: User): void {
        this._createTab({ user });
    }

    /** @private */ _onTabChange(tabId: string): void {
        useDispatcher(this, selectTab({ tabId }));
    }

    /** @private */ _onTabClose(tabId: string): void {
        useDispatcher(this, reloadCancel({ tabId }));
        useDispatcher(this, listDataCancel({ tabId }));
        useDispatcher(this, removeTab({ tabId }));
    }

    /** @private */ _onExportScorecard(): void {
        useDispatcher(this, exportScorecard({}));
    }

    /** @private */ _onToggleUseLiveData(useLiveData: boolean): void {
        useDispatcher(
            this,
            reload({
                tabId: this.state.selectedTab.tabId,
                useLiveData,
            }),
        );
    }

    /** @private */ _onPeriodTypeFocus(): void {
        // maybe move this filtering logic
        const loaded = Array.from(
            this.state.selectedTab.scorecardHistory.keys(),
        );
        const toLoad = this.state.selectedTab.periodTypes
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            .filter((e) => loaded.indexOf(e.periodTypeId) === -1)
            .map((e) => e.periodTypeId);
        if (!toLoad.length) {
            return;
        }
        useDispatcher(
            this,
            reload({
                tabId: this.state.selectedTab.tabId,
                periodTypes: toLoad,
            }),
        );
    }

    /** @private */ _onPeriodTypeChange(periodType: string): void {
        useDispatcher(
            this,
            selectPeriodType({
                tabId: this.state.selectedTab.tabId,
                periodType: parseInt(periodType),
            }),
        );
    }

    /** @private */ _onScorecardChange(scorecardId: string): void {
        // TODO state will need to guard for when the rest of the scorecard data (listData) is still pending
        useDispatcher(
            this,
            selectScorecard({ scorecardId: parseInt(scorecardId) }),
        );
    }

    // TODO revise. need to see if reinflating would be visually quick enough or must inflated views be layered and toggled with maybe visibility+z-index
    private _reinflateCheck(prevState: ScorecardState): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const prevSc =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            prevState && prevState.selectedTab
                ? prevState.selectedTab.selectedScorecard
                : null;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const sc =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state && this.state.selectedTab
                ? this.state.selectedTab.selectedScorecard
                : null;

        const deInflate =
            !prevSc ||
            !prevSc.data ||
            !prevSc.data.templateConfig ||
            !sc ||
            !sc.data ||
            !sc.data.templateConfig ||
            prevSc.data.templateConfig.identifier !==
                sc.data.templateConfig.identifier;
        const reinflate =
            deInflate && sc && sc.data && sc.data.scorecardId != null;

        if (deInflate) {
            this._exportService.inflateHandle = null;
            deflate(this._inflateHdl2);
            deflate(this._inflateHdl);
            this._disposeBridges();
        }

        // if no selected scorecard then all widgets would already be deflated
        if (prevSc !== sc || this._reset) {
            sc && this._dataService.changeScorecardData(sc);
            this._reset = false;
        }

        /** TODO revise. this can be changed to use a single viewRef and reinflate when switching between
         *  scorecard grid and direct reports, but leave for now. mainly because user selections are expected to be
         *  kept when switching between them */

        if (reinflate) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                sc.data.templateConfig &&
                sc.data.templateConfig.containerColour
            ) {
                this.containerColour = sc.data.templateConfig.containerColour;
            }
            /** for scorecardGrid tab */
            const [page1Bridge, page1BridgeSub, page1Injector] =
                this._createBridgeInjectorForPage(1);
            this._bridges[0] = page1Bridge;
            this._bridgeSubs[0] = page1BridgeSub;

            // TODO don't inflate unless this is the selected grid tab. help ease user tab switching
            inflate(
                page1Injector,
                this._factoryResolver,
                this._viewContainerRef,
                widgetTypeComponentMap,
                { paddingRight: '15px', marginBottom: '1rem' },
                sc.data.templateConfig,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                () =>
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this.state.selectedTab &&
                    this.state.selectedTab.pending.export,
                (hdl) => {
                    this._inflateHdl = hdl;
                    this._exportService.inflateHandle = hdl;
                },
            );
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (sc.data.templateConfig && sc.data.templateConfig.widgets) {
                const widgetGrid = sc.data.templateConfig.widgets.find(
                    (widgetGridItem) =>
                        widgetGridItem.widgetType === WidgetType.SCORECARD_GRID,
                );
                if (widgetGrid) {
                    this._templateConfigCopy = JSON.parse(
                        JSON.stringify(sc.data.templateConfig),
                    );

                    // TODO revise, refactor. this is temporary in order to separate the grids
                    // TODO refactor. each grid should be changed to be a WidgetType
                    this._templateConfigCopy.widgets = [widgetGrid];
                    this._templateConfigCopy.layouts = [
                        {
                            itemType: 'container',
                            identifier: 'root',
                            displayOrder: 1,
                            sizes: [{ breakpoint: 1200, width: '100%' }],
                            children: [
                                {
                                    itemType: 'widget',
                                    identifier: widgetGrid.identifier,
                                    displayOrder: 1,
                                    sizes: [
                                        { breakpoint: 1200, width: '100%' },
                                    ],
                                    children: null,
                                },
                            ],
                        },
                    ];

                    /** for directReport tab */
                    const [page2Bridge, page2BridgeSub, page2Injector] =
                        this._createBridgeInjectorForPage(2);
                    this._bridges[1] = page2Bridge;
                    this._bridgeSubs[1] = page2BridgeSub;

                    // TODO don't inflate unless this is the selected grid tab. help ease user tab switching
                    inflate(
                        page2Injector,
                        this._factoryResolver,
                        this._teamGridViewContainerRef,
                        widgetTypeComponentMap,
                        { paddingRight: '15px', marginBottom: '1rem' },
                        this._templateConfigCopy,
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        () =>
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            this.state.selectedTab &&
                            this.state.selectedTab.pending.export,
                        (hdl) => {
                            this._inflateHdl2 = hdl;
                            // TODO fixme. this is overriding the other handle
                            this._exportService.inflateHandle = hdl;
                        },
                    );
                }
            } else {
                // eslint-disable-next-line no-useless-return
                return;
            }
        }
    }

    private _longPendingReloadCheck(prevState: ScorecardState): void {
        if (!prevState.selectedTab || !this.state.selectedTab) {
            return;
        }

        if (
            prevState.selectedTab !== this.state.selectedTab ||
            !this.state.selectedTab.pending.reload
        ) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._longPendingReloadSub &&
                this._longPendingReloadSub.unsubscribe();
            this._longPendingReload = false;
        }

        if (this.state.selectedTab.pending.reload) {
            // _longPendingReload will trigger a message if the wait exceeds 1s
            this._longPendingReloadSub = timer(
                Math.max(
                    0,
                    1000 -
                        (Date.now() -
                            this.state.selectedTab.pending.reloadStartTime),
                ),
            )
                .pipe(
                    take(1),
                    // eslint-disable-next-line no-return-assign
                    tap(() => (this._longPendingReload = true)),
                )
                .subscribe();
        }
    }

    private _reloadCheck(prevState: ScorecardState): void {
        prevState.tabs.forEach((prevTab) => {
            const tab = this.state.tabs.get(prevTab.tabId);
            if (prevTab.pending.reload && tab && !tab.pending.reload) {
                const loaded = Array.from(tab.scorecardDataset.keys());
                const scorecardIds: number[] = [];

                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                tab.periodTypes &&
                    tab.periodTypes.forEach((periodType) => {
                        const dat = tab.scorecardHistory.get(
                            periodType.periodTypeId,
                        );
                        if (!dat) {
                            return;
                        }
                        const ids = dat
                            .map((e) => e.scorecardId)
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            .filter(
                                (e) =>
                                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                                    loaded.indexOf(
                                        datasetKey(periodType.periodTypeId, e),
                                    ) === -1,
                            );
                        scorecardIds.push(...ids);
                    });

                useDispatcher(
                    this,
                    listData({
                        tabId: tab.tabId,
                        scorecardIds,
                        useLiveData: tab.useLiveData,
                    }),
                );
            }
        });
    }

    bridgeCreateTab(selection: ScorecardSelection): void {
        /* currently only the bridge path is expected to transfer the useLiveData state
        from the current tab when creating a new tab */
        this._createTab(selection, this.state.selectedTab.useLiveData);
    }

    bridgeIsExporting(): boolean {
        return this.state.selectedTab.pending.export;
    }

    private _createTab(
        selection: ScorecardSelection,
        useLiveData?: boolean,
        periodTypeIds?: PeriodType[],
    ): void {
        /* createTab action might change to support action chaining
        so that the pre-exist check doesn't need to happen here since createTab already does it */

        let tabId: string;
        let hardReload: boolean;

        try {
            periodTypeIds = periodTypeIds || [
                this.state.selectedTab.selectedPeriodType.periodTypeId,
            ];
        } catch {
            // do nothing
        }
        const userId =
            selection.userId != null ? selection.userId : selection.user.id;
        const tab = Array.from(this.state.tabs.values()).find(
            (e) => e.userId === userId,
        );
        if (tab) {
            // tab exists. switch and hard reload

            tabId = tab.tabId;
            hardReload = true;

            useDispatcher(this, selectTab({ tabId: tab.tabId }));
        } else {
            // tab doesn't exist. create and reload

            tabId = Math.random().toString();

            useDispatcher(
                this,
                createTab({
                    tabId,
                    userId: selection.userId,
                    user: selection.user,
                    authUserId: this._authService.user.id,
                    defaultSelectedSCId: selection.scorecardId,
                }),
            );
        }
        useDispatcher(
            this,
            reload({
                tabId,
                periodTypes: periodTypeIds,
                scorecardId: selection.scorecardId,
                groupId: selection.groupId,
                endDate: selection.endDate,
                useLiveData,
                hard: hardReload,
            }),
        );
    }

    /** @private */ _onResetClick($event: MouseEvent | KeyboardEvent): void {
        this._reset = true;
        $event.stopPropagation();
        useDispatcher(this, resetGrid({}));
    }

    /** @private */ _onExportWidgetsClick(
        $event: MouseEvent | KeyboardEvent,
    ): void {
        $event.stopPropagation();
        let activeDrptTab;
        if (this.state.selectedTab.selectedGridTab === GridTab.SCORECARD_GRID) {
            activeDrptTab = this.state.selectedTab.selectedGridTab;
        } else {
            activeDrptTab =
                this.state.selectedTab.selectedDirectReportDropdownItem;
        }
        useDispatcher(this, widgetsExport({ activeDrptTab }));
    }

    /** @private */ _onGridTabChange(tabRefId: GridTab): void {
        useDispatcher(this, selectedGridTab({ selectedGridTab: tabRefId }));
    }

    /** @private */ _onDropdownGridViewChange(event: GridType): void {
        useDispatcher(
            this,
            selectedDirectReportOption({ selectedDirectReportOption: event }),
        );
    }

    /** @private */ _onExpandAllClick(
        $event: MouseEvent | KeyboardEvent,
    ): void {
        $event.stopPropagation();
        this.bridge.componentTeamgrid.toggleExpandAll();
    }

    // TODO revise, refactor. this is temporary in order to separate the grids
    // TODO refactor. each grid should be changed to be a WidgetType
    private get bridge(): ScoredardBridgeService {
        if (this.state.selectedTab.selectedGridTab === GridTab.SCORECARD_GRID) {
            CMPS_APP_DEF.ENV !== 'prod' && console.debug('bridge 0');
            return this._bridges[0];
        }
        if (
            this.state.selectedTab.selectedGridTab ===
            GridTab.DIRECT_REPORT_VIEW
        ) {
            CMPS_APP_DEF.ENV !== 'prod' && console.debug('bridge 1');
            return this._bridges[1];
        }
        CMPS_APP_DEF.ENV !== 'prod' && console.debug('bridge null');
        return null;
    }

    // TODO revise, refactor. this is temporary in order to separate the grids
    // TODO refactor. each grid should be changed to be a WidgetType
    private _disposeBridges(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._bridges &&
            this._bridges.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                e && e.dispose();
            });
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._bridgeSubs &&
            this._bridgeSubs.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                e && e.unsubscribe();
            });
    }

    // TODO revise, refactor. this is temporary in order to separate the grids
    // TODO refactor. each grid should be changed to be a WidgetType
    private _createBridgeInjectorForPage(
        page: number,
    ): [ScoredardBridgeService, Subscription, Injector] {
        const bridge = new ScoredardBridgeService();
        bridge.componentScorecard.connect(this);
        const bridgeSub = bridge
            .observableForConnect()
            .pipe(
                filter(() => bridge.componentTeamgrid.connected()),
                switchMap(() => bridge.componentTeamgrid.expandAllChange()),
                // eslint-disable-next-line no-return-assign
                tap((value) => (this._expandLabelToggle = !value)),
            )
            .subscribe();
        const injector = Injector.create({
            providers: [
                { provide: ScoredardBridgeService, useValue: bridge },
                { provide: CMPS_SCORECARD_PAGE, useValue: page },
                // these providers are not in a decorator, so is it ok to use an arrow fn value?
                {
                    provide: CMPS_SCORECARD_INITIAL_GRID_TAB,
                    useValue: () => this.state.selectedTab.selectedGridTab,
                },
                {
                    provide: CMPS_SCORECARD_INITIAL_GRID_TYPE,
                    useValue: () =>
                        this.state.selectedTab.selectedDirectReportDropdownItem,
                },
            ],
            parent: this._injector,
        });
        return [bridge, bridgeSub, injector];
    }
}
